// Bedding type configurations + variant pricing keys

export const BEDDING_TYPE_CONFIG = {
  Comforter: {
    label: "Comforter Set",
    description: "Choose your size, then your set size.",
    requires_pc_count: true,
    requires_size: true,
    requires_pillow: false,
  },
  Duvet: {
    label: "Duvet Set",
    description: "Choose your size, then your set size.",
    requires_pc_count: true,
    requires_size: true,
    requires_pillow: false,
  },
  Bedsheet: {
    label: "Bedsheet",
    description: "Premium cotton bedsheet — choose your size and pillow count.",
    requires_pc_count: false,
    requires_size: true,
    requires_pillow: true,
  },
  "Fitted Sheet": {
    label: "Fitted Sheet Set",
    description: "Fitted sheet with elasticated edges — choose size and pillow count.",
    requires_pc_count: false,
    requires_size: true,
    requires_pillow: true,
  },
};

export const COMFORTER_PC_OPTIONS = {
  4: {
    label: "4 pc Set",
    description: "Includes 2 pillow covers, 1 bedsheet, 1 comforter.",
  },
  6: {
    label: "6 pc Set",
    description: "Includes 4 pillow covers, 1 bedsheet, 1 comforter.",
  },
  8: {
    label: "8 pc Set",
    description: "Includes 4 pillow covers, 2 cushion covers, 1 bedsheet, 1 comforter.",
  },
};

export const DUVET_PC_OPTIONS = {
  4: {
    label: "4 pc Set",
    description: "Includes 2 pillow covers, 1 bedsheet, 1 quilt cover.",
  },
  6: {
    label: "6 pc Set",
    description: "Includes 4 pillow covers, 1 bedsheet, 1 quilt cover.",
  },
  8: {
    label: "8 pc Set",
    description: "Includes 4 pillow covers, 2 cushion covers, 1 bedsheet, 1 quilt cover.",
  },
};

// Comforter / Duvet set sizes per bed size. King keeps 6 & 8 pc; Single is a 4 pc set.
export const SET_PC_OPTIONS_BY_SIZE = {
  King: [6, 8],
  Single: [4],
};

export const SET_SIZES = ["King", "Single"];

// Which bed sizes a comforter/duvet is actually sold in. Single only appears once a
// Single 4 pc price has been entered — never as a fallback to the product base price.
export function setSizesFor(product, type) {
  const key = type === "Duvet" ? "duvet_single_4pc" : "comforter_single_4pc";
  const hasSingle = Number(product?.variant_prices?.[key] || 0) > 0;
  return hasSingle ? ["King", "Single"] : ["King"];
}

export function pcOptionsFor(type) {
  return type === "Duvet" ? DUVET_PC_OPTIONS : COMFORTER_PC_OPTIONS;
}

// Pillow options per Bedsheet/Fitted-Sheet size — strict business rule.
// Single → only 1 or 2 pillows. King → only 2 or 4 pillows.
export const PILLOW_OPTIONS_BY_SIZE = {
  Single: [1, 2],
  King: [2, 4],
};

// Sub-collections (used by mega-menu and admin sub-collection dropdown)
export const SUB_COLLECTIONS = {
  bedding: [
    {
      slug: "premium-quality",
      label: "Premium Quality",
      description: "Our finest weave — heirloom-grade comfort.",
      image: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=1600&q=80",
    },
    {
      slug: "export-quality",
      label: "Export Quality",
      description: "Crafted to international standards.",
      image: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/5d96ae127f122e340202493ff75cd939ea9d1aaf56d7d13eb16d26279e7b7519.png",
    },
    {
      slug: "cotton-satin",
      label: "Cotton Satin",
      description: "Crisp cotton with a luxurious satin finish.",
      image: "https://images.unsplash.com/photo-1540518614846-7eded433c457?auto=format&fit=crop&w=1600&q=80",
    },
    {
      slug: "kids-bedding",
      label: "Kids Bedding",
      description: "Playful prints and soft fabrics for little dreamers.",
      image: "https://images.unsplash.com/photo-1573918436949-9b53c9533d0f?auto=format&fit=crop&w=1600&q=80",
    },
  ],
  curtains: [
    {
      slug: "sheer-curtains",
      label: "Sheer Curtains",
      description: "Light, airy panels that filter sunlight gently.",
      image: "https://images.unsplash.com/photo-1502672023488-70e25813eb80?auto=format&fit=crop&w=1600&q=80",
    },
    {
      slug: "velvet-curtains",
      label: "Velvet Curtains",
      description: "Plush, rich velvet for warmth and drama.",
      image: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/9789fdc267bb5924073b16d729ad3de095efc77b79ce1c2eff9b0c6d2308858c.png",
    },
  ],
};

export const SUB_COLLECTION_BY_SLUG = Object.fromEntries(
  Object.values(SUB_COLLECTIONS).flat().map((s) => [s.slug, s])
);

// Variant price keys per category.
// Bedsheet / Fitted Sheet: per-size + per-pillow combo (Single + 1, Single + 2, King + 2, King + 4 — no Single + 4).
// Duvet & Comforter: per pc-count (6/8).
export const VARIANT_KEYS_BY_CATEGORY = {
  bedding: [
    { key: "comforter_6pc", label: "Comforter · King 6 pc Set", placeholder: "e.g. 12000" },
    { key: "comforter_8pc", label: "Comforter · King 8 pc Set", placeholder: "e.g. 14000" },
    { key: "comforter_single_4pc", label: "Comforter · Single 4 pc Set", placeholder: "e.g. 8000" },
    { key: "duvet_6pc", label: "Duvet · King 6 pc Set", placeholder: "e.g. 11000" },
    { key: "duvet_8pc", label: "Duvet · King 8 pc Set", placeholder: "e.g. 13000" },
    { key: "duvet_single_4pc", label: "Duvet · Single 4 pc Set", placeholder: "e.g. 7500" },
    { key: "fitted_sheet_single_1", label: "Fitted Sheet · Single + 1 Pillow", placeholder: "e.g. 3800" },
    { key: "fitted_sheet_single_2", label: "Fitted Sheet · Single + 2 Pillows", placeholder: "e.g. 4500" },
    { key: "fitted_sheet_king_2", label: "Fitted Sheet · King + 2 Pillows", placeholder: "e.g. 6500" },
    { key: "fitted_sheet_king_4", label: "Fitted Sheet · King + 4 Pillows", placeholder: "e.g. 7500" },
    { key: "bedsheet_single_1", label: "Bedsheet · Single + 1 Pillow", placeholder: "e.g. 3500" },
    { key: "bedsheet_single_2", label: "Bedsheet · Single + 2 Pillows", placeholder: "e.g. 4200" },
    { key: "bedsheet_king_2", label: "Bedsheet · King + 2 Pillows", placeholder: "e.g. 5000" },
    { key: "bedsheet_king_4", label: "Bedsheet · King + 4 Pillows", placeholder: "e.g. 6000" },
  ],
  curtains: [
    { key: "curtain_sheer", label: "Sheer Curtain", placeholder: "e.g. 4500" },
    { key: "curtain_velvet", label: "Velvet Curtain", placeholder: "e.g. 7500" },
  ],
};

// Flat list (kept for backwards compat / fromPrice / hasVariantPricing)
export const VARIANT_KEYS = [
  ...VARIANT_KEYS_BY_CATEGORY.bedding,
  ...VARIANT_KEYS_BY_CATEGORY.curtains,
];

// Resolve variant key from current selection.
//   type = "Comforter" | "Duvet" | "Bedsheet" | "Fitted Sheet"
//   pillows = 1 | 2 | 4 (only for Bedsheet / Fitted Sheet)
//   pcCount = 6 | 8 (only for Comforter / Duvet)
//   size = "Single" | "King" (only for Bedsheet / Fitted Sheet)
export function variantKeyFor(type, pillows, pcCount, size) {
  if (type === "Comforter" || type === "Duvet") {
    const prefix = type === "Duvet" ? "duvet" : "comforter";
    // Single is a 4 pc set with its own price; 6 & 8 pc are King (legacy data = King).
    if (String(size || "").toLowerCase() === "single") {
      return pcCount === 4 ? `${prefix}_single_4pc` : null;
    }
    if (pcCount === 8) return `${prefix}_8pc`;
    if (pcCount === 6) return `${prefix}_6pc`;
    return null;
  }
  if (type === "Bedsheet") {
    if (!size || !pillows) return null;
    const s = size.toLowerCase(); // single | king
    // valid combos only
    if (s === "single" && pillows === 1) return "bedsheet_single_1";
    if (s === "single" && pillows === 2) return "bedsheet_single_2";
    if (s === "king" && pillows === 2) return "bedsheet_king_2";
    if (s === "king" && pillows === 4) return "bedsheet_king_4";
    return null;
  }
  if (type === "Fitted Sheet") {
    if (!size || !pillows) return null;
    const s = size.toLowerCase();
    if (s === "single" && pillows === 1) return "fitted_sheet_single_1";
    if (s === "single" && pillows === 2) return "fitted_sheet_single_2";
    if (s === "king" && pillows === 2) return "fitted_sheet_king_2";
    if (s === "king" && pillows === 4) return "fitted_sheet_king_4";
    return null;
  }
  return null;
}

// Map curtain fabric → variant key
export function curtainVariantKey(fabric) {
  if (fabric === "velvet") return "curtain_velvet";
  if (fabric === "sheer") return "curtain_sheer";
  return null;
}

// Read a variant price with backward-compat fallbacks for legacy keys.
// Legacy keys we map (live products may still have these):
//   bedsheet_1 → bedsheet_single_1
//   bedsheet_2 → bedsheet_king_2  (default to king since pre-fix most live SKUs were king)
//   bedsheet_4 → bedsheet_king_4
//   fitted_sheet_2 → fitted_sheet_king_2
//   fitted_sheet_4 → fitted_sheet_king_4
//   duvet → duvet_6pc
const LEGACY_KEY_FALLBACK = {
  bedsheet_single_1: ["bedsheet_1"],
  bedsheet_single_2: [],
  bedsheet_king_2: ["bedsheet_2"],
  bedsheet_king_4: ["bedsheet_4"],
  fitted_sheet_single_1: [],
  fitted_sheet_single_2: [],
  fitted_sheet_king_2: ["fitted_sheet_2"],
  fitted_sheet_king_4: ["fitted_sheet_4"],
  duvet_6pc: ["duvet"],
  duvet_8pc: [],
};

function readVariantPrice(vp, key) {
  if (!vp || !key) return 0;
  const direct = Number(vp[key] || 0);
  if (direct > 0) return direct;
  const fallbacks = LEGACY_KEY_FALLBACK[key] || [];
  for (const fk of fallbacks) {
    const v = Number(vp[fk] || 0);
    if (v > 0) return v;
  }
  return 0;
}

// Read a variant sale price (no legacy fallback — sales are a new feature).
function readVariantSalePrice(vsp, key) {
  if (!vsp || !key) return 0;
  return Number(vsp[key] || 0);
}

// Resolve { base, sale } for the current selection.
// sale = 0 when no active sale (no field set OR sale >= base).
export function priceWithSale(product, config) {
  const fallbackBase = Number(product?.price || 0);
  const fallbackSale = Number(product?.sale_price || 0);
  if (!product || !config) {
    return saleResult(fallbackBase, fallbackSale > 0 && fallbackSale < fallbackBase ? fallbackSale : 0);
  }
  const vp = product.variant_prices || {};
  const vsp = product.variant_sale_prices || {};
  let key = null;
  if (product.category === "bedding") {
    key = variantKeyFor(config.bedding_type, config.pillow_count, config.pc_count, config.size);
  } else if (product.category === "curtains") {
    key = curtainVariantKey(config.fabric);
  }
  if (key) {
    const base = readVariantPrice(vp, key) || fallbackBase;
    const saleRaw = readVariantSalePrice(vsp, key);
    const sale = saleRaw > 0 && saleRaw < base ? saleRaw : 0;
    return saleResult(base, sale);
  }
  // No variant selection → fall back to product-level
  const sale = fallbackSale > 0 && fallbackSale < fallbackBase ? fallbackSale : 0;
  return saleResult(fallbackBase, sale);
}

function saleResult(base, sale) {
  const onSale = sale > 0 && sale < base;
  const final = onSale ? sale : base;
  const discountPct = onSale ? Math.round(((base - sale) / base) * 100) : 0;
  return { base, sale, final, onSale, discountPct };
}

// Resolve current effective price (sale if active, else base) based on selection
export function effectivePrice(product, config) {
  return priceWithSale(product, config).final;
}

// Minimum priced variant (for "from Rs. X" badge). Returns the lowest FINAL price
// across all variants, considering sale prices.
export function fromPrice(product) {
  if (!product) return 0;
  const cat = product.category;
  const keys = VARIANT_KEYS_BY_CATEGORY[cat];
  if (!keys) {
    const base = Number(product.price || 0);
    const sale = Number(product.sale_price || 0);
    return sale > 0 && sale < base ? sale : base;
  }
  const vp = product.variant_prices || {};
  const vsp = product.variant_sale_prices || {};
  const finals = keys
    .map((v) => {
      const base = readVariantPrice(vp, v.key);
      if (base <= 0) return 0;
      const sale = readVariantSalePrice(vsp, v.key);
      return sale > 0 && sale < base ? sale : base;
    })
    .filter((n) => n > 0);
  if (finals.length === 0) {
    const pBase = Number(product.price || 0);
    const pSale = Number(product.sale_price || 0);
    return pSale > 0 && pSale < pBase ? pSale : pBase;
  }
  return Math.min(...finals);
}

// Does any variant of this product have an active sale? Used to show
// "ON SALE" markers in listing cards.
export function hasAnySale(product) {
  if (!product) return false;
  const pBase = Number(product.price || 0);
  const pSale = Number(product.sale_price || 0);
  if (pSale > 0 && pSale < pBase) return true;
  const vp = product.variant_prices || {};
  const vsp = product.variant_sale_prices || {};
  for (const k of Object.keys(vsp)) {
    const base = Number(vp[k] || 0);
    const sale = Number(vsp[k] || 0);
    if (base > 0 && sale > 0 && sale < base) return true;
  }
  return false;
}

// Card-level "from" base (for slashed-through display). Returns the base
// matching the variant whose SALE is the lowest. If no sale, returns same as fromPrice.
export function fromBasePrice(product) {
  if (!product) return 0;
  const cat = product.category;
  const keys = VARIANT_KEYS_BY_CATEGORY[cat];
  if (!keys) return Number(product.price || 0);
  const vp = product.variant_prices || {};
  const vsp = product.variant_sale_prices || {};
  let bestBase = null;
  let bestFinal = Infinity;
  for (const v of keys) {
    const base = readVariantPrice(vp, v.key);
    if (base <= 0) continue;
    const sale = readVariantSalePrice(vsp, v.key);
    const final = sale > 0 && sale < base ? sale : base;
    if (final < bestFinal) {
      bestFinal = final;
      bestBase = base;
    }
  }
  return bestBase ?? Number(product.price || 0);
}

export function hasVariantPricing(product) {
  if (!product) return false;
  const cat = product.category;
  const keys = VARIANT_KEYS_BY_CATEGORY[cat];
  if (!keys) return false;
  const vp = product.variant_prices || {};
  return keys.some((v) => readVariantPrice(vp, v.key) > 0);
}

export function describeConfig(c) {
  if (!c) return "";
  const parts = [];
  if (c.bedding_type) parts.push(c.bedding_type);
  if (c.size) parts.push(c.size);
  if (c.pc_count) parts.push(`${c.pc_count} pc`);
  if (c.pillow_count) parts.push(`${c.pillow_count} ${c.pillow_count === 1 ? "Pillow" : "Pillows"}`);
  if (c.fabric) parts.push(c.fabric === "velvet" ? "Velvet" : "Sheer");
  if (c.curtain_size) parts.push(c.curtain_size);
  return parts.join(" · ");
}

// === Cart bulk discount rule ===
// When the customer has 3+ Bedsheet / Fitted-Sheet units combined across the ENTIRE cart
// (regardless of sub-collection or product), 5% off is applied to every qualifying line.
// Comforters, Duvets, Curtains and Blankets NEVER contribute or receive the discount.
export const BULK_DISCOUNT_RATE = 0.05;
export const BULK_DISCOUNT_MIN_QTY = 3;
const BULK_DISCOUNT_TYPES = new Set(["Bedsheet", "Fitted Sheet"]);

// Is this cart line item a Bedsheet / Fitted Sheet? (Type match only — quantity ignored.)
export function isDiscountableType(item) {
  if (!item || item.category !== "bedding") return false;
  return BULK_DISCOUNT_TYPES.has(item?.config?.bedding_type);
}

// Aggregate quantity of Bedsheet + Fitted-Sheet units across all cart items.
export function totalDiscountableQty(items) {
  if (!Array.isArray(items)) return 0;
  return items.reduce((s, i) => (isDiscountableType(i) ? s + (i.quantity || 0) : s), 0);
}

// Whether the cart currently qualifies for the 5% bulk discount.
export function cartQualifiesForBulk(items) {
  return totalDiscountableQty(items) >= BULK_DISCOUNT_MIN_QTY;
}

// Is THIS line eligible to receive 5% off given the current cart? (type + qualified cart)
export function isBulkEligible(item, items) {
  return isDiscountableType(item) && cartQualifiesForBulk(items);
}

// Discount value contributed by this line when the cart qualifies.
export function lineDiscount(item, items) {
  if (!isBulkEligible(item, items)) return 0;
  return +(item.price * item.quantity * BULK_DISCOUNT_RATE).toFixed(2);
}
